// The nudges a player gets when the opponent is not at this screen (bot or
// online): the your-turn sound, and after idling on their move the AFK chime
// and move hint. Hot-seat gets none of it — both players are looking.
import { AfkTimer } from '@bn-games/ui-shell';
import { TurnSignal } from '@bn-games/audio-core';
import type { SettingsManager } from '../settings/SettingsManager.js';
import type { Match } from './Match.js';
import { afkHint, type AfkHint } from './afkHint.js';

export class TurnCues {
  private readonly turnSignal: TurnSignal;
  private readonly afk: AfkTimer;
  private shown: AfkHint | null = null;

  constructor(private readonly settings: SettingsManager, private readonly match: () => Match | null) {
    this.turnSignal = new TurnSignal({ isEnabled: () => settings.get().turnStartSound });
    this.afk = new AfkTimer({
      isLocalTurn: () => this.applies() && this.match()?.canHumanMove() === true,
      onTimeout: () => this.showHint(),
    }, settings.get().afkTimeoutMs);
  }

  /** The hint to draw right now, if one is showing. */
  hint(): AfkHint | null {
    return this.shown !== null && Date.now() < this.shown.until ? this.shown : null;
  }

  /** The position changed: `canMove` is whether the player here is on move. */
  sync(canMove: boolean): void {
    if (this.applies()) this.turnSignal.update(canMove);
    if (canMove) this.afk.arm();
    else this.afk.disarm();
  }

  /** Any input from the player. */
  poke(): void {
    this.afk.poke();
  }

  /** A move was played: the hint has served its purpose. */
  moved(): void {
    this.shown = null;
    this.afk.poke();
  }

  /** A new game (or none): forget the previous one's turn edge and hint. */
  reset(): void {
    this.turnSignal.reset();
    this.shown = null;
  }

  stop(): void {
    this.afk.disarm();
    this.shown = null;
  }

  private applies(): boolean {
    const match = this.match();
    return match !== null && match.opponent !== 'human';
  }

  private showHint(): void {
    const match = this.match();
    if (match !== null) this.shown = afkHint(match, this.settings.get());
  }
}
